import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useGetSharedAttachments } from '@/hooks/chat/useGetSharedAttachments';
import { LoaderCircle } from 'lucide-react';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { ChatAttachment } from '@my-ipractice/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  open: boolean;
  setOpen: (newValue: boolean) => void;
};

dayjs.extend(localizedFormat);

export const SharedFilesDialog = ({ open, setOpen }: Props) => {
  const { t } = useTranslation();

  const getValidAttachments = () => {
    return attachments?.filter((a) => a.name != null) ?? [];
  };

  const { isLoading, data: attachments } = useGetSharedAttachments();
  const [filteredAttachments, setFilteredAttachments] =
    useState<ChatAttachment[]>(getValidAttachments());
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    if (searchTerm.length == 0) {
      setFilteredAttachments(getValidAttachments());
      return;
    }

    const matches = getValidAttachments().filter((a) =>
      a.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredAttachments(matches);
  }, [searchTerm]);

  useEffect(() => {
    setFilteredAttachments(getValidAttachments());
  }, [attachments]);

  const hasAttachments = !isLoading && (attachments ?? []).length > 0;

  const renderBody = () => {
    if (isLoading || !attachments) {
      return (
        <div className="flex items-center pl-2 space-x-2">
          <div className="w-4 h-4">
            <LoaderCircle className="w-4 h-4 animate-spin text-secondary" />
          </div>
          <h1 className="text-secondary">{t('chat_page.sharedDocumentsLoading')}</h1>
        </div>
      );
    }

    if (!hasAttachments) {
      return <div className="text-secondary">{t('chat_page.sharedDocumentsNoAttachments')}</div>;
    }

    return (
      <div>
        <Input
          autoFocus
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('chat_page.sharedDocumentsSearchForAnAttachmentPlaceholder')}
          className="mb-4"
        />
        {filteredAttachments.length == 0 ? (
          <div className="text-secondary">
            {t('chat_page.sharedDocumentsNoAttachmentsMatchingForSearch')}
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 max-h-[50vh] overflow-y-auto">
            {filteredAttachments.map((a, index) => {
              return (
                <Button
                  key={index}
                  className="h-fit p-6 w-full flex flex-col justify-start text-left"
                  variant="outline"
                  onClick={() => window.open(a.url, '_blank')}
                >
                  <div className="w-full flex flex-col justify-start">
                    <p className="font-bold text-primary underline break-all whitespace-normal">
                      {a.name}
                    </p>
                    <p className="text-sm font-normal text-secondary">
                      {t('chat_page.sharedDocumentsAttachmentMetadata', {
                        date: dayjs(a.sentAt).format('ll'),
                        user: a.sentBy,
                      })}
                    </p>
                  </div>
                </Button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(newValue) => setOpen(newValue)}>
      <DialogContent
        className="min-w-[90%] lg:min-w-[50%]"
        onInteractOutside={() => setOpen(false)}
      >
        <DialogHeader>
          <DialogTitle>{t('chat_page.sharedDocumentsTitle')}</DialogTitle>
        </DialogHeader>
        <div>{renderBody()}</div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('chat_page.sharedDocumentsClose')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
